/**
 * The SaaS plan catalog (round 61, SaaS roadmap step 5) - shared by both sides on
 * purpose: the in-app pricing surface renders FROM this table and the backend gates
 * (artist cap, AI budget) enforce FROM it, so what a plan promises and what it delivers
 * cannot drift apart. Stripe knows these tiers as one Product per plan with a monthly
 * and a yearly Price (see services/stripe.ts for the lookup-key convention and
 * scripts/stripe-bootstrap.ts for creating them); Stripe is the truth for CHARGING,
 * this table is the truth for what the workspace GETS. Selfhost never reads any of it -
 * that edition is free forever on the owner's own keys.
 *
 * The unit-economics rule these numbers encode (PLAN-round50): every paid tier's
 * included AI budget is real vendor cost in cents kept WELL below the plan price, and
 * the round-51 metering engine enforces it - so per-workspace AI spend can never outrun
 * per-workspace revenue. A test asserts budget < price for every paid tier so a future
 * price tweak can't quietly break the guarantee. Prices are USD; the numbers are the
 * owner's round-50 illustrative tiers and are meant to be re-tuned against measured
 * cost before real launch - tuning them HERE retunes the page and the gates together.
 */

import type { PlanQuota, UsageSummary } from "./types.ts";

export type PlanId = "free" | "indie" | "pro" | "studio";
export type PaidPlanId = Exclude<PlanId, "free">;
export type PlanInterval = "monthly" | "yearly";

export interface PlanDef {
	id: PlanId;
	/** The English/backend display name. The UI shows t(`billing.planName.${id}`) instead -
	 *  display copy (names, taglines, feature lines) lives in locales/, not here, so the
	 *  pricing surface is translatable; this table keeps only the NUMERIC truth the gates
	 *  and the pricing page must agree on. */
	name: string;
	/** USD integer cents; 0 on the free tier */
	priceMonthlyCents: number;
	/** USD integer cents for a year (the two-months-free convention); 0 on the free tier */
	priceYearlyCents: number;
	/** How many artist profiles the workspace can hold (creates beyond it are walled, never deleted).
	 *  ⚠ `null` = no limit, which is Studio and nothing else: a manager or a label signs whoever they
	 *  sign, and a number there would be a ceiling somebody eventually pays to be told about. */
	artistLimit: number | null;
	/** Included monthly AI budget at vendor COST, in cents; 0 = AI runs only on the tenant's own key */
	aiBudgetCents: number;
	/** Pitches the workspace may SEND per calendar month; null = uncapped.
	 *  ⚠ Follow-ups are free and are not counted (`services/billing.ts`): a follow-up goes to somebody
	 *  who already got a pitch, so it is not a new stranger contacted. The number is about REACH. */
	outreachPerMonth: number | null;
	/** May arm the 🤖 release autopilot (the hands-off runway) */
	autopilot: boolean;
	/** May create paid ad campaigns from inside the app */
	adCampaigns: boolean;
	/** May RENDER new shorts/reels. Existing files are never touched - see `PLAN_CAPABILITIES`. */
	reels: boolean;
	/**
	 * May publish or RE-publish the link-in-bio page and a release's smart link.
	 *
	 * ⚠ **This was ONE lever for all four public pages until §AK (2026-09-01), and it split for the
	 * strategy review's first item: a free tier that publishes.** The pages a stranger lands on are the
	 * product's only billboard, and a `free` that could publish nothing was a fourteen-day trial with a
	 * dead end after it - no free product at all. So the bio page and the smart link are the free
	 * half (`publishing`), the press kit and the fan kit are the paid half (`kits`), and a free
	 * workspace's pages carry a "Made with Get Heard" link (`pagesBrandMark()` in `services/billing.ts`).
	 * The un-publishes are gated by neither - see `services/billing.ts`.
	 */
	publishing: boolean;
	/** May publish or RE-publish the press kit (EPK) and the fan kit - the paid half of publishing, see above */
	kits: boolean;
	/** May post to a connected Instagram / TikTok / Facebook account */
	posting: boolean;
	/** The mention and follower sweeps run for this workspace */
	mentions: boolean;
	/**
	 * How many releases this plan may HOLD. `null` = no ceiling, which is every paid tier.
	 *
	 * ⚠ **Only `free` has a number, and the number is ONE - which makes this the second half of a rule
	 * the artist ladder already states** (owner, 2026-08-28, after arguing himself out of gating
	 * creation outright and then landing here): *"if the sub is expired, AND the user has a release
	 * already set up, they can't add another one until they resubscribe."* The first one is free
	 * because that is how a lapsed tenant REACHES the walls this app spent a round making good - every
	 * one of them lives inside a release, so somebody who can never make one meets a single refusal on
	 * a button for a thing they have not seen. The SECOND one is where the offer stops, because by then
	 * they have seen what the product does and are asking it to hold a catalog.
	 *
	 * ⚠ **It is a CAP rather than a capability, and that is not a technicality.** `PLAN_CAPABILITIES`
	 * answers *is this switched on at all*, and the honest answer here is "yes, once" - which is a
	 * number. `tests/planMatrix.test.ts` refuses `releases` as a matrix row for that reason and points
	 * at this comment.
	 *
	 * ⚠ **It gates CREATION and nothing else.** A workspace that drops to `free` holding nine releases
	 * keeps all nine, exactly as an over-cap workspace keeps its artists (`billing.pricingNote` says so
	 * out loud); what stops is the tenth.
	 */
	releaseLimit: number | null;
}

/**
 * THE ENTITLEMENT MATRIX (§AD, owner's call 2026-08-26): what stops when a plan lapses.
 *
 * ⚠ **This list exists because the GATE and the MAIL have to read the same one.** Until now the
 * only thing that stopped at expiry was AI, and `life.trial.clockBody` said so out loud -
 * *"the AI features are the part that stops"*. The moment a second thing stops, that sentence is a
 * false promise **inside a paywall**, which is worse than the soft framing it was written to avoid:
 * the reader finds out by being refused something they were told they would keep. So the mail
 * derives its list from here rather than describing it in prose, and a capability added to this
 * union with no locale line fails `tests/planMatrix.test.ts`.
 *
 * ⚠ **The test each row was decided by is not "is it expensive" but "does it cost us money per use,
 * or is it just valuable?"** Both are legitimate reasons to gate and they argue differently in the
 * copy - `reels` and `mentions` are literal per-use cost (CPU-minutes; the operator's shared YouTube
 * quota), `outreach` and `posting` are value. Keep that distinction when writing a wall's sentence:
 * one of them can honestly say "this costs us money to run" and the other cannot.
 *
 * ⚠ **What is NOT here is the load-bearing half.** Everything already made stays: a rendered reel
 * stays on disk, a published page goes on being served, replies to pitches already sent are still
 * read and shown, release-day mail to fans who asked for it still goes out, the export still works.
 * Taking those away is a different promise from refusing to make new ones (§1: a stranger who lands
 * on a smart link and cannot press play is the failure the whole app exists to prevent - and an
 * un-published page punishes the FAN for the artist's lapsed card).
 */
export const PLAN_CAPABILITIES = ["ai", "reels", "outreach", "publishing", "kits", "posting", "mentions", "autopilot", "ads"] as const;

export type PlanCapability = (typeof PLAN_CAPABILITIES)[number];

/**
 * Does this plan include this capability?
 *
 * ⚠ **Exhaustive over the union on purpose** (round 122's pattern): a ninth capability added to
 * `PLAN_CAPABILITIES` fails the typecheck HERE rather than silently inheriting a neighbour's answer,
 * which for a money gate is the difference between a refusal and a giveaway.
 *
 * ⚠ **`ai` and `outreach` are NUMBERS elsewhere and booleans here**, and that is not a fork: this
 * function answers "is this switched on at all", which is the only question the expiry mail and the
 * expired-state wall ask. The amount left is `aiBudgetCents`/`outreachPerMonth` and is a different
 * question with a different reader - a meter, not a gate.
 */
export function planIncludes(def: PlanDef, capability: PlanCapability): boolean {
	switch (capability) {
		case "ai":
			return def.aiBudgetCents > 0;
		case "reels":
			return def.reels;
		case "outreach":
			return def.outreachPerMonth === null || def.outreachPerMonth > 0;
		case "publishing":
			return def.publishing;
		case "kits":
			return def.kits;
		case "posting":
			return def.posting;
		case "mentions":
			return def.mentions;
		case "autopilot":
			return def.autopilot;
		case "ads":
			return def.adCampaigns;
	}
}

/**
 * What a workspace on `from` loses when it lapses to `to` (which is `free`, always, today).
 *
 * ⚠ **Derived by comparing two plan rows rather than listed**, because a list would be a third copy
 * of the matrix and the one nothing enforces. It is also why the trial can be passed as `pro`: the
 * trial derives Pro's levers, so what a trial loses IS what a Pro subscription loses, and the two
 * mails cannot drift apart while they ask this one function.
 */
export function capabilitiesLost(from: PlanId, to: PlanId = "free"): PlanCapability[] {
	return PLAN_CAPABILITIES.filter(cap => planIncludes(PLANS[from], cap) && !planIncludes(PLANS[to], cap));
}

export const PLAN_ORDER: PlanId[] = ["free", "indie", "pro", "studio"];

/**
 * The currency every price in this table is quoted in, lowercase, in Stripe's spelling.
 *
 * ⚠ **It is here rather than in the bootstrap script because two places have to agree and only one
 * of them creates anything** (round 266). `scripts/stripe-bootstrap.ts` writes `currency: "usd"` on
 * a Price, and `services/billingReadiness.ts` has to know what the Price it finds SHOULD say -
 * against a value the operator can also have set by hand in the Dashboard. A checker holding its own
 * copy of the answer is a checker that agrees with itself.
 *
 * ⚠ **A Stripe Price's currency and amount are IMMUTABLE after creation**, which is what makes this
 * constant worth a gate rather than a comment: changing it here does not change what a customer is
 * charged, and nothing about the app would look different. See `LAUNCH.md` - the choice of USD is
 * the owner's and it is one of the two open decisions recorded there.
 */
export const PLAN_CURRENCY = "usd";

/**
 * Is there anything above this plan to sell?
 *
 * ⚠ Derived from `PLAN_ORDER`'s last entry rather than comparing against `"studio"`, because a
 * hardcoded top tier is a sentence that becomes false the day a fourth one is added - and the thing
 * that reads this is a WALL telling a paying customer what to do next (`budgetWallKey`). Getting it
 * wrong means either telling the most expensive customer to upgrade, or failing to tell somebody in
 * the middle that they can.
 */
export function isTopPlan(id: PlanId): boolean {
	return id === PLAN_ORDER[PLAN_ORDER.length - 1];
}

/**
 * ⚠ **THE ARTIST COUNT IS NOT A LEVER ANY MORE, AND THAT IS THE OWNER'S CORRECTION (2026-08-03).**
 *
 * Rounds 50 to 189 tiered this product by how many artist profiles a workspace could hold: 1 / 2 / 6
 * / 20. It reads as a reasonable ladder and it prices almost nobody, because **the overwhelming
 * majority of the people who sign up for this are one artist promoting their own songs** - so the
 * ladder's rungs were invisible to them and the only thing separating $19 from $49 was a number they
 * would never reach. A lever the typical customer cannot feel is not a lever; it is a table that
 * looks like one.
 *
 * So every plan but Studio holds ONE artist, and the tiering moved onto things a working artist
 * meets in their first week: **how much AI is included, how many pitches go out a month, whether the
 * runway can run itself, and whether ads are in the app.** Studio is the only tier where the artist
 * count is mentioned at all, and there it is UNLIMITED (owner, same day): a label or a manager signs
 * whoever they sign, so a number there is a ceiling somebody eventually pays to be told about.
 *
 * ⚠ **Each lever is ENFORCED, and adding one here without its gate is the failure this table is most
 * exposed to** - a plan row is read by a stranger deciding to pay, so a field with no gate is a
 * promise nothing keeps. `tests/planMatrix.test.ts` fails on a lever with no enforcement point - and it means a
 * CALLER, not a declaration: the autopilot's plan check existed and was asked only at arming time,
 * so an armed release went on publishing after the plan that allowed it had lapsed (§AD).
 */
export const PLANS: Record<PlanId, PlanDef> = {
	free: {
		id: "free",
		// ⚠ "Free", not "Trial", since §AK: a plan somebody can STAY on is not a trial, and this row is
		// now a tier a reader is offered (the pricing card's first tile) as well as the state a lapse
		// derives. The trial itself is still fourteen days of Pro - `effectivePlan()` in billing.ts.
		name: "Free",
		priceMonthlyCents: 0,
		priceYearlyCents: 0,
		artistLimit: 1,
		// ⚠ ONE, not zero: a stopped workspace keeps room to make a first release, because that is
		// how it reaches every wall this app has. See `releaseLimit` for the whole argument.
		releaseLimit: 1,
		aiBudgetCents: 0,
		outreachPerMonth: 0,
		autopilot: false,
		adCampaigns: false,
		// ⚠ Every capability off BUT ONE, and `free` is the state a lapsed trial and a canceled
		// subscription both DERIVE (`effectivePlan`) - so this row is the entitlement matrix's other
		// half, and since §AK (2026-09-01) it is ALSO the tier a stranger keeps: the bio page and the
		// smart link stay publishable, with a "Made with Get Heard" mark, because a free page on the
		// internet is the one piece of marketing this product has. The kits are the paid half. What
		// survives an expiry is listed in `PLAN_CAPABILITIES`.
		reels: false,
		publishing: true,
		kits: false,
		posting: false,
		mentions: false
	},
	indie: {
		id: "indie",
		name: "Indie",
		priceMonthlyCents: 1900,
		priceYearlyCents: 19000,
		artistLimit: 1,
		releaseLimit: null,
		aiBudgetCents: 600,
		outreachPerMonth: 150,
		autopilot: true,
		adCampaigns: false,
		reels: true,
		publishing: true,
		kits: true,
		posting: true,
		mentions: true
	},
	pro: {
		id: "pro",
		name: "Pro",
		priceMonthlyCents: 4900,
		priceYearlyCents: 49000,
		artistLimit: 1,
		releaseLimit: null,
		aiBudgetCents: 1600,
		outreachPerMonth: null,
		autopilot: true,
		adCampaigns: true,
		reels: true,
		publishing: true,
		kits: true,
		posting: true,
		mentions: true
	},
	studio: {
		id: "studio",
		name: "Studio",
		priceMonthlyCents: 12900,
		priceYearlyCents: 129000,
		artistLimit: null,
		releaseLimit: null,
		aiBudgetCents: 4500,
		outreachPerMonth: null,
		autopilot: true,
		adCampaigns: true,
		reels: true,
		publishing: true,
		kits: true,
		posting: true,
		mentions: true
	}
};

/**
 * What one AI job costs us, in cents, so a plan can advertise WORK instead of money.
 *
 * Owner's call (2026-08-03): a pricing card must not say "$45 of included AI every month". Two
 * reasons it is a bad line, and the second is the one that decides it. It asks the reader to price
 * something they have never bought - nobody knows what a dollar of AI does - and it invites the
 * arithmetic "so I am paying $129 for $45 of tokens", which is the wrong comparison entirely,
 * because what the plan sells is the pitch being written rather than the tokens it took.
 *
 * ⚠ **This number is a MEASURED average and it will drift**, which is why it lives here as a named
 * constant rather than inside a locale string: a pitch, a caption or a section analysis is roughly a
 * cent of vendor spend at today's model prices. An image generation is 30 to 50 times that, so the
 * copy says "about" and names the text jobs. When model prices move, this moves and every surface
 * that quotes it moves with it.
 */
export const AI_JOB_COST_CENTS = 1;

/**
 * Cents of vendor spend → the number of AI jobs a reader was actually sold.
 *
 * ⚠ **The Account page's AI meter reads a LIVE budget, not a plan row, which is why this exists
 * beside `aiJobsPerMonth` instead of inside it** (2026-08-10). A trial's purse is
 * `config.saasAiBudgetCents` and an operator override is whatever the operator typed, so neither is
 * `PLANS[id].aiBudgetCents` - and a meter that resolved its ceiling through the plan table would tell
 * a tenant on their fourth trial day that they had spent 40% of a budget they are not on.
 *
 * The unit is the point rather than the arithmetic: the pricing card sells "600 AI jobs a month", so
 * the meter that measures what is left of it has to be counting the same thing. Saying "$0.60 of
 * $6.00" on the Account page invites exactly the arithmetic this constant's own doc comment rejects,
 * one screen later and to a reader who has now paid.
 */
export function aiJobsForCents(cents: number): number {
	return Math.round(cents / AI_JOB_COST_CENTS);
}

/**
 * The AI allowance as a QUOTA, in the unit the reader was sold - the meter's arithmetic, and it lives
 * here rather than beside the meter so a node test can reach it (round 284).
 *
 * ⚠ **THE REMAINDER IS THE SERVER'S, NEVER RECOMPUTED, and the first draft proved why.** It read the
 * ceiling as `budgetCents + topupCents` and subtracted `spentCents` - which is wrong the moment
 * granted credit is being CONSUMED, because `topupCents` is what is LEFT of that credit rather than
 * what was granted: `recordAiUsage` draws it down as spend crosses the base budget. A workspace on a
 * 600 budget that has spent 610 with 90 left was told 80, because the ten already taken out of the
 * credit came off a second time. It walks to zero while there is still credit to spend, so the meter
 * says none left and the wall never fires - two readings of one allowance, disagreeing, with nobody
 * positioned to notice. `remainingCents` is that answer on the server and is the one to read.
 *
 * ⚠ **The TOTAL is derived from the two rather than declared**, because the original grant is on no
 * payload: used + remaining reconstructs it exactly, and cannot drift from the remainder it is drawn
 * beside.
 *
 * ⚠ **Cents in, JOBS out.** A meter has to count the thing the pricing card sold, which is the rule
 * `AI_JOB_COST_CENTS` above states: "$0.60 of $6.00" invites exactly the arithmetic that constant's
 * own doc comment rejects, one screen later and to a reader who has now paid.
 *
 * Null means there is no limit to draw: selfhost, the operator, an explicit zero override, or a
 * workspace with no allowance at all - that last reader needs the WALL, which is a different question.
 */
export function aiQuotaFromUsage(usage: UsageSummary, basis: "period" | "total"): PlanQuota | null {
	if (!usage.metered || usage.unmetered) return null;
	const remaining = aiJobsForCents(usage.remainingCents);
	const used = aiJobsForCents(usage.spentCents);
	const limit = used + remaining;
	if (limit <= 0) return null;
	return { used, limit, remaining, basis };
}

/** Roughly how many AI jobs a plan's included budget buys, rounded for a pricing card */
export function aiJobsPerMonth(id: PlanId): number {
	return aiJobsForCents(PLANS[id].aiBudgetCents);
}

/**
 * How full a plan's AI bar is drawn on a PRICING surface, 0-100, scaled to the biggest plan's own
 * allowance.
 *
 * ⚠ **It lives here because there are TWO pricing surfaces and the owner caught them drifting**
 * (2026-08-10: *"the plan picker lacks the pretty gradients that are also present on the landing
 * page… we should probably use the same component or at least the same data source?"*). They already
 * shared the data source - `PLANS` and `planFeatureLines` - and what had NOT been shared was this
 * derivation, which the landing page computed inline. Two copies of an arithmetic that decides how
 * long a bar is drawn is the same class of drift as two copies of a price, and harder to see.
 *
 * ⚠ **A shared COMPONENT is refused, and the refusal is structural rather than lazy**: the landing
 * page is server-rendered Handlebars measured in bytes to first paint and may not import anything
 * from `src/frontend/` (CLAUDE.md's published-page rule), while the picker is a Lit island in a
 * shadow root. `shared/` is the sanctioned crossing point for exactly this, so what crosses is the
 * NUMBERS and the feature lines; each surface still draws them in its own vocabulary.
 *
 * ⚠ **The scale is `Math.max` over the table rather than `PLANS.studio`**: the widest bar has to be
 * full whichever plan turns out to be the biggest, and these tiers have been reshaped once already.
 * Scaling against a round number somebody chose would be a chart with an invented axis.
 */
export function aiAllowanceShare(id: PlanId): number {
	const top = Math.max(...PLAN_ORDER.map(planId => aiJobsPerMonth(planId)));
	return top > 0 ? Math.round((aiJobsPerMonth(id) / top) * 100) : 0;
}

export function isPaidPlan(id: string): id is PaidPlanId {
	return id === "indie" || id === "pro" || id === "studio";
}

/**
 * WHICH feature lines a plan advertises - one list, resolved by whichever side is asking.
 *
 * This lived inside the Settings island until round 137, which was fine while there was exactly one
 * pricing surface. The landing page is the second, and it is read by someone who has not signed up:
 * a plan that promises four things in the app and three on the front door is the `plans.ts` drift
 * problem with a prospective customer holding the receipt. So the LIST lives here beside the numbers
 * it describes, and only the SAYING is per-caller.
 *
 * A resolver rather than a returned `KeyText[]`, deliberately: `say` differs by reader (`t()` in the
 * browser, `serverText(…, locale)` on the landing page, where the locale comes off an Accept-Language
 * header). What the caller may NOT vary is which lines a plan has - that is the drift this function
 * exists to prevent.
 *
 * ⚠ **It took a `money` formatter until 2026-08-03 and does not any more**, which is worth a line
 * because the reason it needed one was good: a currency's shape is per-locale, so the same rule that
 * makes the reader format a `{date}` made it format the budget. The budget stopped being money (see
 * `AI_JOB_COST_CENTS`), so the last per-locale VALUE on these lines is a plain count, which `say`
 * already formats. Prices themselves are still money and are still formatted by the caller.
 */
export function planFeatureLines(id: PlanId, say: (key: string, params?: Record<string, string | number>) => string): string[] {
	const def = PLANS[id];
	const budget = say("billing.featureBudget", { count: aiJobsPerMonth(id) });
	const pitches =
		def.outreachPerMonth === null ? say("billing.featureOutreachUnlimited") : say("billing.featureOutreachCap", { count: def.outreachPerMonth });
	switch (id) {
		// ⚠ THE ONE CARD WHOSE LINES DESCRIBE SOMETHING OTHER THAN ITS OWN GATES, on purpose (owner,
		// 2026-08-03, reshaped by §AK on 2026-09-01). What a reader is offered here is fourteen days of
		// the whole app with no card, and then a plan they can STAY on: the bio page and the smart link,
		// live, with a Get Heard mark. A card listing "0 pitches a month" would be selling the expiry
		// rather than the offer, so the first line is the free tier's one real feature and the trial
		// comes second. ⚠ The self-hosted build is deliberately NOT a line any more: under a price table
		// it is the BYOK escape hatch again, offered to the one reader who was about to pay (§AK); the
		// open-source promise lives in the landing page's footer, where the developer docs are.
		case "free":
			return [
				say("billing.featurePagesFree"),
				say("billing.featureTrialDays", { count: TRIAL_DAYS }),
				say("billing.featureNoCard"),
				say("billing.featureThenChoose")
			];
		case "indie":
			return [budget, pitches, say("billing.featureAutopilot"), say("billing.featurePages")];
		case "pro":
			return [
				budget,
				pitches,
				say("billing.featureAds"),
				say("billing.featureEverythingIn", { plan: say("billing.planName.indie") }),
				say("billing.featureSupport")
			];
		// ⚠ THREE lines rather than four, and the missing one is the point (owner, 2026-08-09). It used
		// to read "Bring your own key and the cap lifts entirely" - the single best reason a heavy user
		// had to stay on a cheaper tier forever, advertised on the most expensive card. There is no
		// honest fourth to put in its place either: everything else Studio has is already on this list
		// or inside "Everything in Pro", and inventing a line would be the promise-with-no-gate this
		// table's header warns about. **A card is allowed to be short.**
		case "studio":
			return [say("billing.featureArtistsUnlimited"), budget, say("billing.featureEverythingIn", { plan: say("billing.planName.pro") })];
	}
}

/**
 * ⚠ **THE AI TOP-UP PACKS ARE GONE, and this note is what stops them being reinvented** (owner,
 * 2026-08-09: *"I don't care for the AI booster packs, I much rather want them to upgrade to a higher
 * plan"*).
 *
 * `TOPUP_PACKS` sold $3 of AI credit for $5 and $10 for $15 - the round-50 escape valve, and its
 * unit economics were never the problem: price > credit, so a pack could not lose money. What it did
 * was compete with the thing the business runs on. A Pro customer who outran their budget every month
 * had a cheaper answer than Studio sitting one click away, and every pack sold was a subscription
 * upgrade that did not happen. **A one-off purchase that substitutes for a recurring one is a
 * discount on the recurring one**, and this product only has three tiers to sell.
 *
 * What survives is the COLUMN, `workspaces.ai_topup_cents`, and everything that draws it down - see
 * `services/staffActions.ts`. That is a different feature wearing the same storage: an operator
 * granting goodwill credit to somebody the app let down is a support gesture, not a SKU, and it is
 * the one lever Crew has when a tenant hits a wall for a reason that is ours. ⚠ Do not "finish the
 * removal" by dropping the column.
 */
export const MANUAL_CREDIT_MAX_CENTS = 1000;

/**
 * The no-card reverse trial (PLAN-round50): a fresh signup gets full Pro features for
 * this many days with a small included AI budget (config.saasAiBudgetCents - the trial
 * is a time-boxed metered budget, nothing more). When the clock or the budget runs out
 * the workspace drops to the free tier; nothing is ever locked or deleted.
 */
export const TRIAL_DAYS = 14;

/**
 * Outlet sending is paid-only (owner, 2026-10-24). Trials can prepare drafts but send no initial
 * pitches, follow-ups or replies. The former ten-pitch allowance invited account farming, and its
 * free-follow-up exception was another sending path; the shared mail seam now checks payment too.
 * Keep a numeric allowance so existing quota readings and disabled buttons agree with that gate.
 */
export const TRIAL_OUTREACH_TOTAL = 0;
